// FILE: userDirectoryReactQuery.ts
// Purpose: The single React Query definition for the user directory, so every surface that needs a name or a face shares one cache entry.
// Exports: `userDirectoryQueryKey`, `userDirectoryQueryOptions`, `useUserDirectory`, `useUserDirectoryMap`.

import type { UserDirectoryEntry, UserId } from "@taskin-code/contracts";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { UserDirectoryClient } from "~/auth/UserDirectoryClient";

export const userDirectoryQueryKey = ["userDirectory"] as const;

/**
 * The directory is small, changes only when an account is created or renamed,
 * and is about to be read by several unrelated surfaces at once (the assignee
 * picker, every message row that needs a face). One minute of staleness is
 * what stops those from each issuing their own request on mount.
 */
const DEFAULT_STALE_TIME = 60_000;

export function userDirectoryQueryOptions(input?: { enabled?: boolean; staleTime?: number }) {
  return queryOptions({
    queryKey: userDirectoryQueryKey,
    queryFn: () => UserDirectoryClient.list(),
    enabled: input?.enabled ?? true,
    staleTime: input?.staleTime ?? DEFAULT_STALE_TIME,
  });
}

export function useUserDirectory(input?: { enabled?: boolean; staleTime?: number }) {
  return useQuery(userDirectoryQueryOptions(input));
}

/**
 * The directory keyed by id, which is the shape every consumer that starts
 * from a `userId` actually wants — "who is `authorUserId`, and do they have a
 * picture?". Memoised per fetch so a timeline of a hundred rows builds the map
 * once rather than scanning the array once per row.
 */
export function useUserDirectoryMap(input?: {
  enabled?: boolean;
  staleTime?: number;
}): ReadonlyMap<UserId, UserDirectoryEntry> {
  const query = useUserDirectory(input);
  return useMemo(() => new Map((query.data ?? []).map((entry) => [entry.id, entry])), [query.data]);
}
